import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { Eyebrow, Pill } from '../Vitrine/VitrineUi';
import { VitrineLayout, useDemoButton } from '../Vitrine/VitrineLayout';
import { BlocPilote } from '../Vitrine/ModulesEtFormules';
import { CaptureCarousel } from '../Vitrine/CaptureCarousel';
import { PROFILS_EXEMPLES } from '../Vitrine/vitrineAccueil';
import { INTRO_MODULES, PAGES_METIERS } from '../Vitrine/vitrineMetiers';
import { CONSEILLERE } from '../Vitrine/vitrineConseillere';
import { ConseillereExemple } from '../Vitrine/ConseillereExemple';

const slug = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const Contenu: React.FC<{ cle: string }> = ({ cle }) => {
    const { tokens } = useAmbiance();
    const demoButton = useDemoButton();
    const profil = PROFILS_EXEMPLES.find((p) => p.cle === cle)!;
    const page = PAGES_METIERS[cle];
    const groupes = page.captures.reduce<{ nom: string; captures: typeof page.captures }[]>((acc, c) => { const dernier = acc[acc.length - 1]; if (dernier && dernier.nom === c.module) dernier.captures.push(c); else acc.push({ nom: c.module, captures: [c] }); return acc; }, []);
    const autres = PROFILS_EXEMPLES.filter((p) => p.cle !== cle);

    return (
        <div style={{ ['--acc' as string]: tokens.acc }}>
            <AmbianceBand veil="linear-gradient(180deg, rgba(5,6,10,.30) 0%, rgba(5,6,10,.18) 60%, rgba(5,6,10,.45) 100%)">
                <section className="max-w-[1160px] mx-auto px-5 py-16 md:py-20 text-center">
                    <div className="text-[12px] font-bold uppercase tracking-[.1em] text-content-muted mb-4">{profil.onglet}</div>
                    <h1 className="mx-auto max-w-[820px] text-[34px] md:text-[54px] font-semibold leading-[1.06] tracking-[-0.02em] mb-5">{page.titre}</h1>
                    <p className="mx-auto max-w-[560px] text-[16px] text-content-muted leading-relaxed mb-7">{page.intro}</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {demoButton('lg')}
                        <Pill variant="secondary" size="lg" to="/formules">Voir les formules</Pill>
                    </div>
                </section>
            </AmbianceBand>

            {/* Le parcours : de la demande du client à votre quotidien */}
            <section className="border-b border-border bg-surface">
                <div className="max-w-[1160px] mx-auto px-5 py-9 md:py-11">
                    <div className="text-[11px] font-bold uppercase tracking-[.1em] text-content-muted mb-6 text-center">Comment ça marche</div>
                    <ol className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6">
                        {page.flux.map((f, i) => (
                            <li key={f} className="relative flex flex-col items-center text-center px-2">
                                {i < page.flux.length - 1 && <span className="hidden md:block absolute top-[19px] left-[calc(50%+26px)] right-[calc(-50%+26px)] h-px bg-border" aria-hidden="true" />}
                                <span className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-[15px] font-bold text-white shadow-md mb-3" style={{ background: tokens.acc }} aria-hidden="true">{i + 1}</span>
                                <span className="text-[14px] font-semibold leading-snug max-w-[200px]">{f}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Sommaire : les modules de cet espace, un clic pour y aller (reste sous l'en-tête) */}
            <nav className="sticky top-[88px] lg:top-[68px] z-30 border-b border-border bg-surface/90 backdrop-blur" aria-label="Modules de cet espace">
                <div className="max-w-[1160px] mx-auto px-5 py-3 flex items-center gap-2 overflow-x-auto">
                    <span className="hidden md:block shrink-0 text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mr-2">Dans cet espace</span>
                    {groupes.map((g, k) => (
                        <a key={g.nom} href={`#module-${slug(g.nom)}`} className="shrink-0 inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3.5 py-1.5 text-[13px] font-semibold hover:border-content transition-colors">
                            <span className="text-[11px] tabular-nums" style={{ color: tokens.acc }}>{String(k + 1).padStart(2, '0')}</span>{g.nom}
                        </a>
                    ))}
                </div>
            </nav>

            {/* Un module après l'autre : son numéro, son titre, son introduction, puis ses captures */}
            {groupes.map((g, k) => (
                <section key={g.nom} id={`module-${slug(g.nom)}`} className={`scroll-mt-40 ${k % 2 ? 'bg-surface-app border-y border-border' : ''}`}>
                    <div className="max-w-[1160px] mx-auto px-5 pt-14 md:pt-20 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-10 gap-y-3 items-end">
                        <div className="flex items-end gap-4">
                            <span className="text-[56px] md:text-[84px] font-bold leading-[.85] tracking-[-0.04em] tabular-nums" style={{ color: tokens.acc, opacity: .85 }} aria-hidden="true">{String(k + 1).padStart(2, '0')}</span>
                            <div>
                                <Eyebrow>{INTRO_MODULES[g.nom]?.quoi || 'Module'}</Eyebrow>
                                <h2 className="text-[30px] md:text-[42px] font-semibold leading-[1.05] tracking-[-0.02em]">{g.nom}</h2>
                            </div>
                        </div>
                        {INTRO_MODULES[g.nom] && <p className="max-w-[560px] text-[15.5px] text-content-muted leading-relaxed md:pb-1">{INTRO_MODULES[g.nom].intro}</p>}
                    </div>
                    <CaptureCarousel captures={g.captures} />
                </section>
            ))}

            {CONSEILLERE[cle] && <ConseillereExemple exemples={CONSEILLERE[cle]} />}

            {/* Les modules, en une ligne */}
            <section className="bg-surface-app border-y border-border">
                <div className="max-w-[1160px] mx-auto px-5 py-12 md:py-14">
                    <Eyebrow>Ce que vous allumez</Eyebrow>
                    <ul className="flex flex-wrap gap-2 mb-4">
                        {profil.modules.map((m) => (
                            <li key={m.nom} className={`rounded-full border px-3.5 py-1.5 text-[13.5px] font-semibold ${m.bientot ? 'border-dashed border-border text-content-muted' : 'border-border bg-surface'}`}>
                                {m.nom}{m.bientot && <span className="ml-1.5 text-[10px] uppercase tracking-[.06em]">Bientôt</span>}
                            </li>
                        ))}
                    </ul>
                    <p className="text-[13.5px] text-content-muted max-w-[640px]">{page.formule}</p>
                </div>
            </section>

            <BlocPilote />

            <section className="max-w-[1160px] mx-auto px-5 pb-20">
                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-3">Vous êtes plutôt…</div>
                <div className="flex flex-wrap gap-2">
                    {autres.map((p) => <Pill key={p.cle} variant="secondary" to={`/metiers/${p.cle}`}>{p.onglet}</Pill>)}
                    <Pill variant="secondary" to="/formules">Les formules</Pill>
                </div>
            </section>
        </div>
    );
};

/** /metiers/<métier> : une page par métier (restaurateur, prestataire, fournisseur). */
export const VitrineMetierPage: React.FC = () => {
    const { metier = '' } = useParams();
    const profil = PROFILS_EXEMPLES.find((p) => p.cle === metier);
    if (!profil) return <Navigate to="/" replace />;
    return (
        <VitrineLayout title={`L'IAmani — ${profil.onglet}`}>
            <Contenu cle={metier} />
        </VitrineLayout>
    );
};

export default VitrineMetierPage;
